// src/modules/operaciones/inventarios/dashboard/FiltrosBar.js
//
// Barra de filtros globales del dashboard: Bodegas, Tipo de merma, Fabricantes, Familias,
// "Solo con merma" y "Ocultar sin movimiento". Se queda fija arriba al hacer scroll (sticky).
//
// USO (cualquiera de las dos formas)
//   const d = useDashboard(parseado);
//   <FiltrosBar dashboard={d} />
//
//   <FiltrosBar
//     catalogos={d.catalogos} filtros={d.filtros}
//     setFiltro={d.setFiltro} toggleFiltro={d.toggleFiltro} limpiarFiltros={d.limpiarFiltros}
//     cantidadFiltros={d.cantidadFiltros} hayFiltros={d.hayFiltros} ventaNoFiltrable={d.ventaNoFiltrable}
//   />
//
// Forma de los catálogos (los entrega analizarMermas):
//   bodegas:     [{ clave: "SAN JORGE", etiqueta: "San Jorge" }]
//   tipos:       [{ id: "conocida", etiqueta: "Merma conocida" }]
//   fabricantes: ["Comercial + Bios", ...]      familias: ["CONCENTRADO", ...]
// Un filtro vacío = "todos". Las claves que se guardan en `filtros` son: clave (bodegas),
// id (tipos) y el texto tal cual (fabricantes, familias).
//
// PROPS EXTRA
//   sticky      false → no se fija arriba (defecto true)
//   className   clases extra

import React, { useEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle, Check, ChevronDown, Filter, RotateCcw, Search } from "lucide-react";

/* ========================================================================== */
/* Utilidades                                                                 */
/* ========================================================================== */

const sinTildes = (s) =>
  String(s ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

// Convierte cualquier forma de catálogo en [{ valor, etiqueta }]
function aOpciones(lista, { valor, etiqueta } = {}) {
  return (Array.isArray(lista) ? lista : [])
    .map((x) => {
      if (x !== null && typeof x === "object") {
        const v = x[valor] ?? x.clave ?? x.id ?? x.valor ?? x.nombre;
        return { valor: v, etiqueta: String(x[etiqueta] ?? x.etiqueta ?? x.nombre ?? v) };
      }
      return { valor: x, etiqueta: String(x) };
    })
    .filter((o) => o.valor !== undefined && o.valor !== null && o.valor !== "");
}

const UMBRAL_BUSQUEDA = 8; // desde cuántas opciones aparece el cuadro de búsqueda

/* ========================================================================== */
/* Selector múltiple (desplegable)                                            */
/* ========================================================================== */

function SelectorMultiple({ etiqueta, opciones, seleccion, onToggle, onQuitar, alinear = "izquierda" }) {
  const [abierto, setAbierto] = useState(false);
  const [busqueda, setBusqueda] = useState("");
  const contenedor = useRef(null);

  // Cierra con clic fuera o Esc
  useEffect(() => {
    if (!abierto) return undefined;
    const fuera = (e) => {
      if (contenedor.current && !contenedor.current.contains(e.target)) setAbierto(false);
    };
    const tecla = (e) => {
      if (e.key === "Escape") setAbierto(false);
    };
    document.addEventListener("mousedown", fuera);
    document.addEventListener("keydown", tecla);
    return () => {
      document.removeEventListener("mousedown", fuera);
      document.removeEventListener("keydown", tecla);
    };
  }, [abierto]);

  useEffect(() => {
    if (!abierto) setBusqueda("");
  }, [abierto]);

  const visibles = useMemo(() => {
    const q = sinTildes(busqueda).trim();
    return q ? opciones.filter((o) => sinTildes(o.etiqueta).includes(q)) : opciones;
  }, [opciones, busqueda]);

  const marcados = new Set(seleccion);
  const cantidad = seleccion.length;
  const activo = cantidad > 0;

  return (
    <div ref={contenedor} className="relative w-full sm:w-auto">
      <button
        type="button"
        onClick={() => setAbierto((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={abierto}
        className={`w-full sm:w-auto inline-flex items-center justify-between gap-2 rounded-lg border px-3 py-1.5 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 ${
          activo
            ? "border-blue-300 bg-blue-50 text-blue-800"
            : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
        }`}
      >
        <span className="flex items-center gap-2">
          {etiqueta}
          {activo && (
            <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1 rounded-full bg-blue-600 text-white text-xs font-semibold">
              {cantidad}
            </span>
          )}
        </span>
        <ChevronDown className={`w-4 h-4 transition-transform ${abierto ? "rotate-180" : ""}`} aria-hidden="true" />
      </button>

      {abierto && (
        <div
          className={`absolute z-30 mt-1 w-full sm:w-72 bg-white border border-gray-200 rounded-lg shadow-lg ${
            alinear === "derecha" ? "sm:right-0" : "sm:left-0"
          }`}
        >
          {opciones.length > UMBRAL_BUSQUEDA && (
            <div className="p-2 border-b border-gray-100">
              <div className="relative">
                <Search className="w-4 h-4 text-gray-400 absolute left-2 top-1/2 -translate-y-1/2" aria-hidden="true" />
                <input
                  type="text"
                  value={busqueda}
                  onChange={(e) => setBusqueda(e.target.value)}
                  placeholder={`Buscar ${etiqueta.toLowerCase()}...`}
                  autoFocus
                  className="w-full pl-8 pr-2 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          )}

          <ul role="listbox" aria-multiselectable="true" className="max-h-60 overflow-y-auto py-1">
            {visibles.length === 0 && <li className="px-3 py-2 text-sm text-gray-500">Sin resultados</li>}
            {visibles.map((o) => {
              const marcado = marcados.has(o.valor);
              return (
                <li key={String(o.valor)} role="option" aria-selected={marcado}>
                  <button
                    type="button"
                    onClick={() => onToggle(o.valor)}
                    className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-left text-gray-700 hover:bg-gray-50"
                  >
                    <span
                      className={`flex-shrink-0 inline-flex items-center justify-center w-4 h-4 rounded border ${
                        marcado ? "bg-blue-600 border-blue-600 text-white" : "border-gray-300 bg-white"
                      }`}
                    >
                      {marcado && <Check className="w-3 h-3" aria-hidden="true" />}
                    </span>
                    <span className="truncate" title={o.etiqueta}>
                      {o.etiqueta}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>

          <div className="flex items-center justify-between px-3 py-2 border-t border-gray-100 text-xs text-gray-500">
            <span>{activo ? `${cantidad} seleccionado(s)` : "Todos incluidos"}</span>
            {activo && (
              <button type="button" onClick={onQuitar} className="font-medium text-blue-600 hover:text-blue-800">
                Quitar selección
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ========================================================================== */
/* Interruptor                                                                */
/* ========================================================================== */

function Interruptor({ etiqueta, activo, onCambio }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={activo}
      onClick={() => onCambio(!activo)}
      className="inline-flex items-center gap-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-full"
    >
      <span
        className={`relative inline-flex w-9 h-5 flex-shrink-0 rounded-full transition-colors ${
          activo ? "bg-blue-600" : "bg-gray-300"
        }`}
      >
        <span
          className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${
            activo ? "translate-x-4" : ""
          }`}
        />
      </span>
      <span className={activo ? "font-medium text-gray-900" : ""}>{etiqueta}</span>
    </button>
  );
}

/* ========================================================================== */
/* Componente                                                                 */
/* ========================================================================== */

export function FiltrosBar({ dashboard = null, sticky = true, className = "", ...propios }) {
  // Acepta el objeto del hook completo o las props sueltas (las sueltas tienen prioridad)
  const p = { ...(dashboard || {}), ...propios };
  const { catalogos, filtros, setFiltro, toggleFiltro, limpiarFiltros, cantidadFiltros, hayFiltros, ventaNoFiltrable } = p;

  const opciones = useMemo(
    () => ({
      bodegas: aOpciones(catalogos?.bodegas, { valor: "clave", etiqueta: "etiqueta" }),
      tipos: aOpciones(catalogos?.tipos, { valor: "id", etiqueta: "etiqueta" }),
      fabricantes: aOpciones(catalogos?.fabricantes),
      familias: aOpciones(catalogos?.familias),
    }),
    [catalogos]
  );

  if (!catalogos || !filtros) return null;

  const selectores = [
    { clave: "bodegas", etiqueta: "Bodegas" },
    { clave: "tipos", etiqueta: "Tipo de merma" },
    { clave: "fabricantes", etiqueta: "Fabricantes" },
    { clave: "familias", etiqueta: "Familias" },
  ].filter((s) => opciones[s.clave].length > 0);

  return (
    <div
      className={`${sticky ? "sticky top-0 z-20" : ""} bg-white/95 backdrop-blur border border-gray-200 rounded-xl shadow-sm px-3 py-3 ${className}`}
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <span className="hidden sm:inline-flex items-center gap-1.5 text-sm font-medium text-gray-500">
          <Filter className="w-4 h-4" aria-hidden="true" />
          Filtros
        </span>

        {selectores.map((s, i) => (
          <SelectorMultiple
            key={s.clave}
            etiqueta={s.etiqueta}
            opciones={opciones[s.clave]}
            seleccion={filtros[s.clave] || []}
            onToggle={(valor) => toggleFiltro(s.clave, valor)}
            onQuitar={() => setFiltro(s.clave, [])}
            // Los últimos desplegables se abren hacia la izquierda para no salirse de la pantalla
            alinear={i >= selectores.length - 2 && selectores.length > 2 ? "derecha" : "izquierda"}
          />
        ))}

        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 w-full sm:w-auto sm:ml-2">
          <Interruptor
            etiqueta="Solo con merma"
            activo={Boolean(filtros.soloConMerma)}
            onCambio={(v) => setFiltro("soloConMerma", v)}
          />
          <Interruptor
            etiqueta="Ocultar sin movimiento"
            activo={Boolean(filtros.ocultarSinMovimiento)}
            onCambio={(v) => setFiltro("ocultarSinMovimiento", v)}
          />
        </div>

        {hayFiltros && (
          <button
            type="button"
            onClick={limpiarFiltros}
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 sm:ml-auto"
          >
            <RotateCcw className="w-4 h-4" aria-hidden="true" />
            Limpiar filtros
            <span className="text-xs text-gray-400">({cantidadFiltros})</span>
          </button>
        )}
      </div>

      {ventaNoFiltrable && (
        <p className="mt-2 flex items-start gap-1.5 text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-md px-2 py-1.5">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" aria-hidden="true" />
          <span>
            La venta no se puede separar por bodega ni por tipo de merma: el % merma/venta compara la merma
            filtrada con la venta completa, así que es aproximado.
          </span>
        </p>
      )}
    </div>
  );
}

export default FiltrosBar;